<template>
  <div class="pupr">

    <!-- Hero -->
    <header class="pupr__hero">
      <div class="pupr__hero-bg"></div>
      <div class="pupr__hero-overlay"></div>
      <div class="pupr__hero-glow"></div>

      <div class="pupr__hero-inner">
        <div class="pupr__hero-badge">
          <span class="pupr__hero-badge-dot">
            <span class="pupr__hero-badge-ping"></span>
          </span>
          {{ $t('blog.puPrime.badge') }}
        </div>

        <div class="pupr__hero-icon-wrap">
          <i class="fas fa-gift pupr__hero-icon"></i>
        </div>

        <h1 class="pupr__hero-title" v-html="$t('blog.puPrime.heroTitle')"></h1>
        <p class="pupr__hero-lead" v-html="$t('blog.puPrime.heroLead')"></p>

        <div class="pupr__hero-deadline">
          <i class="fas fa-clock"></i>
          {{ $t('blog.puPrime.heroDeadline') }} <strong>{{ $t('blog.puPrime.heroDeadlinePeriod') }}</strong>
        </div>

        <div class="pupr__hero-slider" aria-hidden="true">
          <div class="pupr__hero-slider-track">
            <span v-for="(reward, i) in [...rewards, ...rewards]" :key="`pupr-slide-${i}`">
              <span class="pupr__hero-slider-icon">{{ reward.icon }}</span>
              {{ reward.name }}
            </span>
          </div>
        </div>

        <div class="pupr__hero-actions">
          <a
            href="#pupr-brokers"
            class="pupr__hero-btn pupr__hero-btn--primary"
          >
            <i class="fas fa-user-plus"></i>
            {{ $t('blog.puPrime.puBtn') }}
          </a>
          <a href="#rewards" class="pupr__hero-btn pupr__hero-btn--ghost">
            <i class="fas fa-arrow-down"></i>
            {{ $t('blog.puPrime.heroBtn') }}
          </a>
        </div>
      </div>

      <div class="pupr__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z" fill="#f2f0ea"/>
        </svg>
      </div>
    </header>

    <!-- Rewards section -->
    <section id="rewards" class="pupr__section pupr__section--cream">
      <div class="pupr__shell">
        <div class="pupr__section-header">
          <div class="pupr__section-eyebrow">{{ $t('blog.puPrime.rewardsEyebrow') }}</div>
          <h2 class="pupr__section-title">{{ $t('blog.puPrime.rewardsTitle') }}</h2>
          <div class="pupr__divider"></div>
        </div>

        <div class="pupr__rewards-grid">
          <div
            v-for="(reward, i) in rewards"
            :key="i"
            class="pupr__reward-card"
            :class="{ 'pupr__reward-card--highlight': reward.highlight }"
          >
            <div class="pupr__reward-icon">{{ reward.icon }}</div>
            <div class="pupr__reward-body">
              <h3 class="pupr__reward-name">{{ reward.name }}</h3>
              <span class="pupr__reward-level">{{ reward.level }}</span>
            </div>
            <div v-if="reward.highlight" class="pupr__reward-badge">Đỉnh cao</div>
          </div>
        </div>

        <div class="pupr__cash-note">
          <i class="fas fa-coins"></i>
          <p v-html="$t('blog.puPrime.cashNoteText')"></p>
        </div>
      </div>
    </section>

    <!-- How to join -->
    <section class="pupr__section pupr__section--white">
      <div class="pupr__shell">
        <div class="pupr__section-header">
          <h2 class="pupr__section-title">{{ $t('blog.puPrime.howTitle') }}</h2>
          <div class="pupr__divider"></div>
        </div>

        <div class="pupr__steps">
          <div v-for="(step, i) in steps" :key="i" class="pupr__step">
            <div class="pupr__step-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="pupr__step-body">
              <h3 class="pupr__step-title">{{ step.title }}</h3>
              <p class="pupr__step-desc">{{ step.desc }}</p>
            </div>
          </div>
        </div>

        <div class="pupr__warning">
          <i class="fas fa-exclamation-triangle"></i>
          <p v-html="$t('blog.puPrime.warningText')"></p>
        </div>

        <div class="pupr__terms">
          <i class="fas fa-file-alt"></i>
          <a href="https://vn.puprime.pro/terms-and-conditions/vietnampremierreward-terms/" target="_blank" rel="noopener noreferrer">
            {{ $t('blog.puPrime.termsLink') }}
          </a>
        </div>

        <p class="pupr__disclaimer">{{ $t('blog.puPrime.disclaimer') }}</p>
      </div>
    </section>

    <!-- Open account -->
    <section class="pupr__section pupr__section--cream">
      <div class="pupr__shell">
        <div class="pupr__section-header">
          <h2 class="pupr__section-title">{{ $t('blog.puPrime.openAccountTitle') }}</h2>
          <p class="pupr__section-sub">{{ $t('blog.puPrime.openAccountSub') }}</p>
          <div class="pupr__divider"></div>
        </div>

        <div id="pupr-brokers" class="pupr__brokers">

          <!-- PU Prime -->
          <div class="pupr__broker-card pupr__broker-card--pu">
            <div class="pupr__broker-logo">{{ $t('blog.puPrime.puLogoText') }}</div>
            <h3 class="pupr__broker-title">{{ $t('blog.puPrime.puCardTitle') }}</h3>

            <div class="pupr__broker-info">
              <div class="pupr__broker-row">
                <span class="pupr__broker-label">{{ $t('blog.puPrime.linkLabel') }}</span>
                <a href="https://puvip.co/niANNr" target="_blank" rel="noopener noreferrer" class="pupr__broker-link">
                  puvip.co/niANNr <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
              <div class="pupr__broker-row pupr__broker-row--code">
                <span class="pupr__broker-label">{{ $t('blog.puPrime.codeLabel') }}</span>
                <div class="pupr__broker-code-wrap">
                  <span class="pupr__broker-code" @click="copyCode('SHCcapital')">
                    SHCcapital <i class="fas fa-copy"></i>
                  </span>
                  <button class="pupr__guide-btn" @click="openGuide('pu')">
                    <i class="fas fa-images"></i>
                    {{ $t('blog.puPrime.guideBtn') }}
                  </button>
                </div>
              </div>
            </div>

            <a href="https://puvip.co/niANNr" target="_blank" rel="noopener noreferrer" class="pupr__broker-btn pupr__broker-btn--pu">
              <i class="fas fa-user-plus"></i>
              {{ $t('blog.puPrime.puBtn') }}
            </a>
            <p class="pupr__broker-note">{{ $t('blog.puPrime.brokerNote') }}</p>
          </div>

          <!-- Vantage -->
          <div class="pupr__broker-card pupr__broker-card--vantage">
            <div class="pupr__broker-logo">{{ $t('blog.puPrime.vantageLogoText') }}</div>
            <h3 class="pupr__broker-title">{{ $t('blog.puPrime.vantageCardTitle') }}</h3>

            <div class="pupr__broker-info">
              <div class="pupr__broker-row">
                <span class="pupr__broker-label">{{ $t('blog.puPrime.linkLabel') }}</span>
                <a href="https://vigco.co/la-com/vi/SHCcapital" target="_blank" rel="noopener noreferrer" class="pupr__broker-link">
                  vigco.co/SHCcapital <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
              <div class="pupr__broker-row pupr__broker-row--code">
                <span class="pupr__broker-label">{{ $t('blog.puPrime.codeLabel') }}</span>
                <div class="pupr__broker-code-wrap">
                  <span class="pupr__broker-code" @click="copyCode('SHCcapital')">
                    SHCcapital <i class="fas fa-copy"></i>
                  </span>
                  <button class="pupr__guide-btn" @click="openGuide('vantage')">
                    <i class="fas fa-images"></i>
                    {{ $t('blog.puPrime.guideBtn') }}
                  </button>
                </div>
              </div>
            </div>

            <a href="https://vigco.co/la-com/vi/SHCcapital" target="_blank" rel="noopener noreferrer" class="pupr__broker-btn pupr__broker-btn--vantage">
              <i class="fas fa-user-plus"></i>
              {{ $t('blog.puPrime.vantageBtn') }}
            </a>
            <p class="pupr__broker-note">{{ $t('blog.puPrime.brokerNote') }}</p>
          </div>

        </div>

        <!-- Contact CTA -->
        <div class="pupr__cta">
          <p>{{ $t('blog.puPrime.ctaText') }}</p>
          <button class="pupr__cta-btn" @click="goToContact">
            <i class="fas fa-comments"></i>
            {{ $t('blog.puPrime.ctaBtn') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Copy toast -->
    <div v-if="showCopyToast" class="pupr__toast">
      <i class="fas fa-check-circle"></i> {{ $t('blog.puPrime.toastMsg') }}
    </div>

    <!-- Guide image modal -->
    <div v-if="guideModal" class="pupr__modal" @click="closeGuide">
      <div class="pupr__modal-box" @click.stop>
        <button class="pupr__modal-close" @click="closeGuide">
          <i class="fas fa-times"></i>
        </button>
        <p class="pupr__modal-caption">{{ guideModal.caption }}</p>
        <img :src="guideModal.src" :alt="guideModal.caption" class="pupr__modal-img" />
      </div>
    </div>

  </div>
</template>

<script src="./pu-prime-vietnam-reward.js"></script>
<style src="./pu-prime-vietnam-reward.scss" lang="scss"></style>
